<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>產品庫存盤點</h5>
      </b-card-header>

      <b-card-body>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-input
              v-model="searchQuery"
              placeholder="搜尋產品..."
              class="mb-2"
            />
          </b-col>
        </b-row>

        <b-overlay
          :show="loading"
          spinner-variant="primary"
          spinner-type="grow"
          spinner-small
          rounded="sm"
        >
          <b-table
            ref="stocktakingTable"
            :items="products"
            :fields="tableColumns"
            responsive
            show-empty
            empty-text="沒有找到相關產品"
          >
            <!-- 產品名稱 -->
            <template #cell(name)="data">
              {{ data.item.name }}
              <br />
              <small class="text-muted">{{ data.item.model }}</small>
            </template>

            <!-- 目前庫存 -->
            <template #cell(quantity)="data">
              {{ data.item.quantity || 0 }}
            </template>

            <!-- 盤點數量 -->
            <template #cell(updated_quantity)="data">
              <b-form-input
                v-model.number="stocktakingData[data.item.id]"
                type="number"
                min="0"
                placeholder="輸入實際數量"
              />
            </template>

            <!-- 差異 -->
            <template #cell(difference)="data">
              <span
                :class="getDifferenceClass(data.item.id, data.item.quantity)"
              >
                {{ calculateDifference(data.item.id, data.item.quantity) }}
              </span>
            </template>
          </b-table>

          <div class="text-right mt-2">
            <b-button
              variant="primary"
              @click="confirmStocktaking"
              :disabled="!hasChanges"
            >
              確認盤點
            </b-button>
          </div>
        </b-overlay>
      </b-card-body>
    </b-card>

    <!-- 添加確認 Modal -->
    <b-modal
      id="modal-stocktaking-confirm"
      title="更新摘要"
      ok-title="更新"
      cancel-title="取消"
      @ok="submitStocktaking"
      centered
    >
      <b-table
        :items="summaryItems"
        :fields="summaryFields"
        responsive
        show-empty
        empty-text="沒有任何更改"
      >
        <template #cell(difference)="data">
          <span
            :class="
              getDifferenceClass(data.item.id, data.item.current_quantity)
            "
          >
            {{ data.value }}
          </span>
        </template>
      </b-table>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BTable,
  BButton,
  BOverlay,
  BModal,
} from "bootstrap-vue";
import { ref, computed, onUnmounted, watch } from "@vue/composition-api";
import store from "@/store";
import productStoreModule from "../productStoreModule";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BButton,
    BOverlay,
    BModal,
  },

  setup(props, { root }) {
    const PRODUCT_STORE_MODULE_NAME = "product";

    // 註冊 module
    if (!store.hasModule(PRODUCT_STORE_MODULE_NAME)) {
      store.registerModule(PRODUCT_STORE_MODULE_NAME, productStoreModule);
    }

    // 組件卸載時取消註冊
    onUnmounted(() => {
      if (store.hasModule(PRODUCT_STORE_MODULE_NAME)) {
        store.unregisterModule(PRODUCT_STORE_MODULE_NAME);
      }
    });

    const loading = ref(false);
    const searchQuery = ref("");
    const products = ref([]);
    const stocktakingData = ref({});

    const tableColumns = [
      { key: "name", label: "產品名稱" },
      { key: "quantity", label: "目前庫存量" },
      { key: "updated_quantity", label: "盤點數量" },
      { key: "difference", label: "差異" },
    ];

    const summaryFields = [
      { key: "name", label: "產品名稱" },
      { key: "current_quantity", label: "目前庫存" },
      { key: "new_quantity", label: "盤點數量" },
      { key: "difference", label: "差異" },
    ];

    // 載入產品列表
    const fetchProductsList = () => {
      loading.value = true;
      store
        .dispatch("product/fetchProducts", { q: searchQuery.value })
        .then((response) => {
          products.value = response.data.products;
          loading.value = false;
        })
        .catch(() => {
          loading.value = false;
        });
    };

    // 計算差異
    const calculateDifference = (productId, currentQuantity) => {
      const updatedQuantity = stocktakingData.value[productId];
      if (updatedQuantity === undefined) return "-";
      return updatedQuantity - (currentQuantity || 0);
    };

    // 差異顏色樣式
    const getDifferenceClass = (productId, currentQuantity) => {
      const difference = calculateDifference(productId, currentQuantity);
      if (difference === "-") return "";
      return difference > 0
        ? "text-success"
        : difference < 0
        ? "text-danger"
        : "";
    };

    // 檢查是否有變更
    const hasChanges = computed(() => {
      return Object.keys(stocktakingData.value).length > 0;
    });

    // 獲取摘要數據
    const summaryItems = computed(() => {
      return Object.entries(stocktakingData.value).map(([id, newQty]) => {
        const product = products.value.find((p) => p.id === parseInt(id));
        return {
          id: parseInt(id),
          name: product.name,
          current_quantity: product.quantity || 0,
          new_quantity: newQty,
          difference: newQty - (product.quantity || 0),
        };
      });
    });

    // 修改確認盤點函數
    const confirmStocktaking = () => {
      if (hasChanges.value) {
        root.$bvModal.show("modal-stocktaking-confirm");
      }
    };

    // 實際提交盤點數據
    const submitStocktaking = () => {
      const items = Object.entries(stocktakingData.value).map(
        ([productId, updatedQuantity]) => ({
          product_id: parseInt(productId),
          updated_quantity: updatedQuantity,
        })
      );

      if (!items.length) return;

      loading.value = true;
      store
        .dispatch("product/stocktaking", { items })
        .then((response) => {
          loading.value = false;
          stocktakingData.value = {};
          fetchProductsList();

          root.$swal({
            text: response.data.message,
            icon: "success",
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        })
        .catch((error) => {
          loading.value = false;
          root.$swal({
            text: error.response?.data?.message || "盤點失敗",
            icon: "error",
            customClass: {
              confirmButton: "btn btn-primary",
            },
            buttonsStyling: false,
          });
        });
    };

    // 初始化
    fetchProductsList();

    watch(searchQuery, () => {
      fetchProductsList();
    });

    return {
      loading,
      searchQuery,
      products,
      tableColumns,
      stocktakingData,
      calculateDifference,
      getDifferenceClass,
      hasChanges,
      summaryFields,
      summaryItems,
      confirmStocktaking,
      submitStocktaking,
    };
  },
};
</script>

<style scoped>
.text-success {
  color: #28c76f !important;
}

.text-danger {
  color: #ea5455 !important;
}
</style>
